import coolremoteSDK from "coolremote-sdk";
import {
  Action,
  action,
  actionOn,
  ActionOn,
  Computed,
  computed,
  Thunk,
  thunk,
} from "easy-peasy";
import _ from "lodash";
import { isUndefined, values, filter as filterLodash, isEmpty } from "lodash";
import moment from "moment-timezone";
import { t } from "ttag";
import alertSeverities from "../constants/alertSeverities";
import urlFlagMap from "../constants/urlToFlagMapping";
import { checkDateInRange } from "../services/timeService";
import configService from "@services/configService";
import { IRootStoreModel } from "./RootStore";
import { getTrimmedText } from "@services/stringServices";
const severties: any = alertSeverities;
export interface IAlert {
  id: string;
  type: string;
  eventTime: number;
  site: string;
  trigger: string | null;
  isRead: boolean;
  data: any;
  initiator: string | null;
  clearTime?: number;
  clearReason?: number;
  shortId: string;
  status: number;
  snoozeUsername?: string;
  snoozeEndTimeUTC?: number;
  snoozeRuleId?: string;
  snoozeScope?: string;
  snoozeNote?: string;
  snoozeUnitId?: string;
  snoozeSystemId?: string;
}
export interface IAlertParseOptions {
  openAlertsOnly?: boolean;
  withAllOpenAlerts?: boolean;
  onlyInputAlerts?: boolean;
}

export interface IAlertMap {
  [key: string]: IAlert;
}

export interface IAlertsModel {
  allOpenAlerts: IAlertMap;
  allAlerts: IAlertMap;
  newerAlertsExist: boolean;
  initialize: Action<IAlertsModel, any>;
  onInitialized: ActionOn<IAlertsModel, IRootStoreModel>;
  fetchUpdatedAlert: Thunk<IAlertsModel, { id: string }>;
  updateNewerAlertsExist: Action<IAlertsModel, { isExist: boolean }>;
  sendAlertEmail: Thunk<IAlertsModel, { id: string; emails: string[] }>;
  deleteAlert: Thunk<IAlertsModel, string>;
  getAlertsWithParams: Thunk<IAlertsModel, { status: any; }>;
  setAcknowledgedEvent: Thunk<IAlertsModel, { id: string; data: any }>;
  _storeDeleteAlert: Action<IAlertsModel, string>;
  getAlertsPaginated: Thunk<
    IAlertsModel,
    {
      filter: string;
      id: string;
      page: number;
      rowsPerPage: number;
      startDate: any;
      endDate: any;
      types: any;
      codes: any;
      statuses: any;
    }
  >;
  _storeFetchUpdatedAlert: Action<
    IAlertsModel,
    { alertId: string; alert: IAlert }
  >;
  isResolved: Computed<IAlertsModel, (id: string) => boolean>;
  timeResolved: Computed<IAlertsModel, (id: string) => number | undefined>;
  getAlertsByFilter: Computed<
    IAlertsModel,
    (
      filter: {
        customerId?: string | null | undefined;
        siteId?: string | null | undefined;
        systemId?: string | null | undefined;
        unitId?: string | null | undefined;
      },
      showOpenAlertsOnly?: boolean,
      showBothAckAndOpen?: boolean,
      hideUnitDisconnected?: boolean
    ) => any,
    IRootStoreModel
  >;
  getAlertFeildsNamesAndIds: Computed<IAlertsModel, any>;

  parsedAlerts: Computed<IAlertsModel>;

  updateAlert: Thunk<IAlertsModel, { id: string; data: any }>;
  getAlertsByFilters: Thunk<
    IAlertsModel,
    {
      startTime?: any;
      endTime?: any;
      status?: any;
      withAllOpenAlerts?: boolean;
      openAlertsOnly?: boolean;
    }
  >;
  getNotificationAlerts: Thunk<IAlertsModel, { notificationShortId: string }>;
  updateAlertStore: Action<IAlertsModel, { id: string; data: any }>;
  closeEvent: Thunk<IAlertsModel, string>;
  /**
   * Snoozes a specific alert event based on the specified duration and scope.
   * Calls the SDK to snooze the event and updates the local store state.
   *
   * @param payload - The payload for the snooze operation.
   * @param payload.triggerEventId - The ID of the event/alert triggering this snooze.
   * @param payload.triggerEventType - The type code of the trigger event/alert.
   * @param payload.duration - The duration string for the snooze (e.g. "1h", "2d", "1w" or raw minutes).
   * @param payload.system - Optional system ID if the snooze applies system-wide.
   * @param payload.unit - Optional unit ID if the snooze applies unit-wide.
   * @param payload.errorCode - The error code associated with the alert.
   * @param payload.note - An optional custom reason/note for the snooze.
   * @returns A promise resolving to the SDK snooze event response.
   */
  snoozeAlert: Thunk<IAlertsModel, { triggerEventId?: string; triggerEventType?: number; duration: string; system?: string; unit?: string; errorCode: string; note?: string }>;

  /**
   * Cancels a previously active snooze for an alert.
   * Calls the SDK to remove the snooze rule and clears the snooze properties in the local store.
   *
   * @param payload - The payload for canceling the snooze.
   * @param payload.id - The ID of the event/alert.
   * @param payload.snoozeRuleId - The ID of the snooze rule created in the SDK.
   * @returns A promise resolving when the operation completes.
   */
  cancelSnoozeAlert: Thunk<IAlertsModel, { id: string; snoozeRuleId?: string }>;

  /**
   * Reducer action to update the local state of open and all alerts with the snooze details.
   *
   * @param payload - The snooze details to apply to the state.
   * @param payload.id - The ID of the event/alert being snoozed.
   * @param payload.snoozeUsername - The name of the user who performed the snooze action.
   * @param payload.snoozeEndTimeUTC - The millisecond UTC timestamp when the snooze will expire.
   * @param payload.snoozeRuleId - The unique rule ID generated by the SDK.
   * @param payload.snoozeScope - The scope of the snooze ("system" or "unit").
   * @param payload.snoozeNote - The optional note text explaining the snooze.
   */
  _storeSnoozeAlert: Action<IAlertsModel, { id: string; ids?: string[]; snoozeUsername: string; snoozeEndTimeUTC: number; snoozeRuleId: string; snoozeScope: string; snoozeNote?: string; snoozeUnitId?: string; snoozeSystemId?: string }>;

  /**
   * Reducer action to clear snooze details from the local state for a specific alert.
   *
   * @param payload - The details of the alert to cancel snooze for.
   * @param payload.id - The ID of the event/alert to clear.
   */
  _storeCancelSnoozeAlert: Action<IAlertsModel, { id: string; ids?: string[] }>;
}

/**
 * Snoozes a specific alert event based on the specified duration and scope.
 * Calls the SDK to snooze the event and updates the local store state.
 *
 * @param payload - The payload for the snooze operation.
 * @param payload.triggerEventId - The ID of the event/alert triggering this snooze.
 * @param payload.triggerEventType - The type code of the trigger event/alert.
 * @param payload.duration - The duration string for the snooze (e.g. "1h", "2d", "1w" or raw minutes).
 * @param payload.system - Optional system ID if the snooze applies system-wide.
 * @param payload.unit - Optional unit ID if the snooze applies unit-wide.
 * @param payload.errorCode - The error code associated with the alert.
 * @param payload.note - An optional custom reason/note for the snooze.
 * @returns A promise resolving to the SDK snooze event response.
 */
export type SnoozeAlertAction = (payload: {
  triggerEventId?: string;
  triggerEventType?: number;
  duration: string;
  system?: string;
  unit?: string;
  errorCode: string;
  note?: string;
}) => Promise<any>;

/**
 * Cancels a previously active snooze for an alert.
 * Calls the SDK to remove the snooze rule and clears the snooze properties in the local store.
 *
 * @param payload - The payload for canceling the snooze.
 * @param payload.id - The ID of the event/alert.
 * @param payload.snoozeRuleId - The ID of the snooze rule created in the SDK.
 * @returns A promise resolving when the operation completes.
 */
export type CancelSnoozeAlertAction = (payload: {
  id: string;
  snoozeRuleId?: string;
}) => Promise<void>;


export const alertsModel: IAlertsModel = {
  allOpenAlerts: {},
  allAlerts: {},
  newerAlertsExist: false,
  parsedAlerts: computed([(state, storeState: any) => storeState], state => {
    return parseAlerts({}, {}, state);
  }),

  initialize: action((state, payload) => {
    const { events } = payload;
    state.allAlerts = { ...state.allAlerts, ...events };
    state.newerAlertsExist = false;
  }),

  onInitialized: actionOn(
    actions => [actions.initialize],
    () => { }
  ),
  sendAlertEmail: thunk((actions, payload) => {
    return coolremoteSDK.Services.sendEventEmail(
      { emails: payload.emails },
      payload.id
    );
  }),
  _storeDeleteAlert: action((state, payload) => {
    if (!state.allOpenAlerts[payload]) {
      return;
    }
    const { [payload]: _, ...otherAlerts } = state.allOpenAlerts;

    state.allOpenAlerts = otherAlerts;
  }),
  deleteAlert: thunk(async (actions, payload) => {
    await coolremoteSDK.Event.delete(payload);

    actions._storeDeleteAlert(payload);
  }),
  getAlertsWithParams: thunk(async (actions, payload, { getStoreState }) => {
    if (configService.get('performance_v1')) {
      const rootState: any = getStoreState();

      const siteId = rootState.selections.selections?.siteId;
      if (!siteId) {
        return;
      }
      coolremoteSDK.Site.getSiteEvents(
        siteId,
        {
          status: payload.status
        }
      ).then((events: any) => {
        actions.initialize({ events });
      });
    } else {
      coolremoteSDK.Event.getEventsWithArrayParams({
        status: payload.status
      }).then((events: any) => {
        actions.initialize({ events });
      });
    }
  }),
  setAcknowledgedEvent: thunk(async (actions, payload) => {
    const { id, data } = payload;
    return coolremoteSDK.Event.setAcknowledgedEvent(id, data);
  }),
  updateAlert: thunk(async (actions, payload) => {
    const { id, data } = payload;
    return coolremoteSDK.Event.update(id, data);
  }),
  getAlertsPaginated: thunk(async (actions, payload) => {
    const res = await coolremoteSDK.Event.getEventsPaginated(payload),
      alerts = res.retAlertObj,
      { length } = res,
      data = { alerts, length };
    return data;
  }),

  fetchUpdatedAlert: thunk(async (actions, payload) => {
    const alert = await coolremoteSDK.Event.fetch(payload.id);

    actions._storeFetchUpdatedAlert({ alertId: payload.id, alert });
  }),

  updateNewerAlertsExist: action((state, payload) => {
    const { isExist } = payload;
    state.newerAlertsExist = isExist;
  }),

  _storeFetchUpdatedAlert: action((state, payload) => {
    const { allOpenAlerts, allAlerts } = state;

    const { alert, alertId } = payload;
    if (alert.status === 1) {
      state.allOpenAlerts[alertId] = alert;
    } else {
      delete allOpenAlerts[alertId];
      state.allOpenAlerts = allOpenAlerts;
    }
  }),
  /**
   * Reducer action to update the local state of open and all alerts with the snooze details.
   *
   * @param state - The local Easy Peasy state slice.
   * @param payload - The snooze details to apply to the state.
   */
  _storeSnoozeAlert: action((state, payload) => {
    const { id, ids, snoozeUsername, snoozeEndTimeUTC, snoozeRuleId, snoozeScope, snoozeNote, snoozeUnitId, snoozeSystemId } = payload;
    const targetIds = ids || [id];
    targetIds.forEach((alertId: string) => {
      if (state.allOpenAlerts[alertId]) {
        state.allOpenAlerts[alertId].snoozeUsername = snoozeUsername;
        state.allOpenAlerts[alertId].snoozeEndTimeUTC = snoozeEndTimeUTC;
        state.allOpenAlerts[alertId].snoozeRuleId = snoozeRuleId;
        state.allOpenAlerts[alertId].snoozeScope = snoozeScope;
        state.allOpenAlerts[alertId].snoozeNote = snoozeNote;
        state.allOpenAlerts[alertId].snoozeUnitId = snoozeUnitId;
        state.allOpenAlerts[alertId].snoozeSystemId = snoozeSystemId;
      }
      if (state.allAlerts[alertId]) {
        state.allAlerts[alertId].snoozeUsername = snoozeUsername;
        state.allAlerts[alertId].snoozeEndTimeUTC = snoozeEndTimeUTC;
        state.allAlerts[alertId].snoozeRuleId = snoozeRuleId;
        state.allAlerts[alertId].snoozeScope = snoozeScope;
        state.allAlerts[alertId].snoozeNote = snoozeNote;
        state.allAlerts[alertId].snoozeUnitId = snoozeUnitId;
        state.allAlerts[alertId].snoozeSystemId = snoozeSystemId;
      }
    });
  }),

  /**
   * Reducer action to clear snooze details from the local state for a specific alert.
   *
   * @param state - The local Easy Peasy state slice.
   * @param payload - The details of the alert to cancel snooze for.
   */
  _storeCancelSnoozeAlert: action((state, payload) => {
    const { id, ids } = payload;
    const targetIds = ids || [id];
    targetIds.forEach((alertId: string) => {
      if (state.allOpenAlerts[alertId]) {
        delete state.allOpenAlerts[alertId].snoozeUsername;
        delete state.allOpenAlerts[alertId].snoozeEndTimeUTC;
        delete state.allOpenAlerts[alertId].snoozeRuleId;
        delete state.allOpenAlerts[alertId].snoozeScope;
        delete state.allOpenAlerts[alertId].snoozeNote;
        delete state.allOpenAlerts[alertId].snoozeUnitId;
        delete state.allOpenAlerts[alertId].snoozeSystemId;
      }
      if (state.allAlerts[alertId]) {
        delete state.allAlerts[alertId].snoozeUsername;
        delete state.allAlerts[alertId].snoozeEndTimeUTC;
        delete state.allAlerts[alertId].snoozeRuleId;
        delete state.allAlerts[alertId].snoozeScope;
        delete state.allAlerts[alertId].snoozeNote;
        delete state.allAlerts[alertId].snoozeUnitId;
        delete state.allAlerts[alertId].snoozeSystemId;
      }
    });
  }),
  isResolved: computed([state => state.allOpenAlerts], allOpenAlerts => id => {
    if (
      isUndefined(allOpenAlerts[id]) ||
      !isUndefined(allOpenAlerts[id].clearTime)
    ) {
      return true;
    } else {
      return false;
    }
  }),

  timeResolved: computed(
    [state => state.allOpenAlerts],
    allOpenAlerts => id => {
      if (isUndefined(allOpenAlerts[id])) {
        return undefined;
      } else {
        return allOpenAlerts[id].clearTime;
      }
    }
  ),

  getAlertsByFilter: computed(
    [
      state => state.parsedAlerts,
      (state, storeState: any) => storeState.types,
      (state, storeState: any) => storeState.units.allUnits,
    ],

    (parsedAlerts, types, allUnits) =>
      (
        filter,
        showOpenAlertsOnly = false,
        showBothAckAndOpen = true,
        hideUnitDisconnected = true
      ) => {
        const { eventTypes } = types;
        const alertsToFilter = Object.values(parsedAlerts);
        const { customerId, siteId, systemId, unitId } = filter;
        return filterLodash(values(alertsToFilter), (alert: any) => {
          if (showBothAckAndOpen && alert.status === "Closed") {
            return false;
          }

          if (showOpenAlertsOnly && alert.status !== "Open") {
            return false;
          }

          if (
            hideUnitDisconnected &&
            alert.type === eventTypes.unitDisconnected
          ) {
            return false;
          }

          if (
            !hideUnitDisconnected &&
            alert.type === eventTypes.unitsDisconnected
          ) {
            return false;
          }
          if (
            !hideUnitDisconnected &&
            alert.type === eventTypes.scheduleFailures
          ) {
            return false;
          }

          if (!!systemId && !alert.systemId) {
            return false;
          }

          if (!!unitId && alert.unitId !== unitId) {
            return false;
          }

          // if (!alert.isPowerMeter && !alert.isSensor && alert.unitId) {
          //   return false;
          // }

          if (
            !!systemId &&
            (alert.systemId
              ? alert.systemId !== systemId
              : alert.systemIds.length > 1 &&
              !alert.systemIds.includes(systemId))
          ) {
            return false;
          }

          if (!!siteId && alert.siteId !== siteId) {
            return false;
          }

          if (!!customerId && alert.customer !== customerId) {
            return false;
          }

          return true;
        });
      }
  ),
  getAlertFeildsNamesAndIds: computed(
    [
      (state, storeState: any) => storeState.sites.allSites,
      (state, storeState: any) => storeState.units.allUnits,
      (state, storeState: any) => storeState.units.getUnitsBySyncId,
      (state, storeState: any) => storeState.devices.allDevices,
      (state, storeState: any) => storeState.systems.allSystems,
      (state, storeState: any) => ({
        eventTypes: storeState.types.eventTypes,
        unitTypes: storeState.types.unitTypes,
        entityTypes: storeState.types.entityTypes,
      }),
      (state, storeState: any) => storeState.sensors.allSensors,
      (state, storeState: any) => storeState.devices.allLines,
      (state, storeState: any) => storeState.powerMeters.allPowerMeters,
      (state, storeState: any) => storeState.sites.sitesFlags,
      (state, storeState: any) => storeState.groups.allGroups,
      (state, storeState: any) => storeState.users.userPreferences?.alertLogUnitDisplay,
    ],
    (
      allSites,
      allUnits,
      unitsBySyncId,
      allDevices,
      allSystems,
      types,
      sensors,
      lines,
      powerMeters,
      sitesFlags,
      groups,
      alertLogUnitDisplay
    ) =>
      (alert: any) => {
        return mapAlertFields(
          allSites,
          allDevices,
          allSystems,
          allUnits,
          unitsBySyncId,
          types,
          alert,
          sensors,
          lines,
          powerMeters,
          sitesFlags,
          groups,
          alertLogUnitDisplay
        );
      }
  ),
  getAlertsByFilters: thunk(
    async (
      actions,
      { withAllOpenAlerts, openAlertsOnly, ...payload }: any = {
        withAllOpenAlerts: true,
        payload: "",
      },
      { getStoreState, getStoreActions }
    ) => {
      try {
        if (configService.get('performance_v1')) {
          const rootState: any = getStoreState();

          const siteId = rootState.selections.selections?.siteId || rootState.selections.mobileSelections?.siteId;
          if (!siteId) {
            return;
          }

          const alerts = await coolremoteSDK.Site.getSiteEvents(
            siteId,
            payload
          );

          actions.updateNewerAlertsExist({ isExist: false });

          return parseAlerts(
            alerts,
            { withAllOpenAlerts, openAlertsOnly },
            getStoreState()
          );
        } else {
          const alerts = await coolremoteSDK.Event.getEvents(payload);
          actions.updateNewerAlertsExist({ isExist: false });
          return parseAlerts(
            alerts,
            { withAllOpenAlerts, openAlertsOnly },
            getStoreState()
          );
        }
      } catch (ex: any) {
        const { message } = ex
        const allActions: any = getStoreActions()
        const { addMessage, setGlobalErrorReport } = allActions.errorMessage
        setGlobalErrorReport({ messageDescription: 'error fetching alerts ' + message })
        addMessage({ message: 'There was an error while loading alerts' })
      }
    }
  ),
  getNotificationAlerts: thunk(async (actions, payload, { getStoreState }) => {
    const rawAlerts: any = await coolremoteSDK.Services.getNotificationAlerts(
      payload.notificationShortId
    );
    const parsed = parseAlerts(rawAlerts, { onlyInputAlerts: true }, getStoreState());
    return { parsed, rawAlerts };
  }),

  updateAlertStore: action((state, payload) => {
    const { id, data } = payload;
    if (state.allAlerts[id]) {
      state.allAlerts[id] = { ...state.allAlerts[id], ...data };
    }
    if (state.allOpenAlerts[id]) {
      state.allOpenAlerts[id] = { ...state.allOpenAlerts[id], ...data };
    }
  }),
  closeEvent: thunk(async (actions, payload: any) => {
    return coolremoteSDK.Event.closeEvent(payload);
  }),
  /**
   * Snoozes a specific alert event based on the specified duration and scope.
   * Calls the SDK to snooze the event and updates the local store state.
   *
   * @param actions - Easy-peasy local actions.
   * @param payload - The payload for the snooze operation.
   * @param helpers - Easy-peasy helpers, including getStoreState.
   * @returns A promise resolving to the SDK snooze event response.
   */
  snoozeAlert: thunk(async (actions, payload: any, { getStoreState }) => {
    const response = await coolremoteSDK.Event.snoozeEvent(payload);

    const state: any = getStoreState();
    const username = state.users.me?.username || "Unknown User";

    let durationMinutes = 60;
    if (payload.duration) {
      const val = parseInt(payload.duration, 10);
      if (payload.duration.endsWith('h')) durationMinutes = val * 60;
      else if (payload.duration.endsWith('d')) durationMinutes = val * 24 * 60;
      else if (payload.duration.endsWith('w')) durationMinutes = val * 7 * 24 * 60;
      else durationMinutes = val;
    }
    const snoozeEndTimeUTC = moment().add(durationMinutes, 'minutes').valueOf();
    const snoozeRuleId = response?.id || "mock-rule-id";

    const parsedAlerts = state.alerts.parsedAlerts || {};
    const alertIdsToSnooze: string[] = [];
    const scope = payload.system ? "system" : "unit";
    const targetSystemId = payload.system;
    const targetUnitId = payload.unit;

    Object.values(parsedAlerts).forEach((alert: any) => {
      if (scope === "system" && targetSystemId) {
        if (alert.systemId === targetSystemId || (alert.systemIds && alert.systemIds.includes(targetSystemId))) {
          alertIdsToSnooze.push(alert.id);
        }
      } else if (scope === "unit" && targetUnitId) {
        if (alert.unitId === targetUnitId || (alert.unitIds && alert.unitIds.includes(targetUnitId))) {
          alertIdsToSnooze.push(alert.id);
        }
      }
    });

    if (alertIdsToSnooze.length === 0 && payload.triggerEventId) {
      alertIdsToSnooze.push(payload.triggerEventId);
    }

    actions._storeSnoozeAlert({
      id: payload.triggerEventId,
      ids: alertIdsToSnooze,
      snoozeUsername: username,
      snoozeEndTimeUTC,
      snoozeRuleId: snoozeRuleId,
      snoozeScope: scope,
      snoozeNote: payload.note || "",
      snoozeUnitId: payload.unit,
      snoozeSystemId: payload.system
    });
    return response;
  }),

  /**
   * Cancels a previously active snooze for an alert.
   * Calls the SDK to remove the snooze rule and clears the snooze properties in the local store.
   *
   * @param actions - Easy-peasy local actions.
   * @param payload - The payload for canceling the snooze.
   * @returns A promise resolving when the operation completes.
   */
  cancelSnoozeAlert: thunk(async (actions, payload: any, { getStoreState }) => {
    if (payload.snoozeRuleId && payload.snoozeRuleId !== "mock-rule-id") {
      await coolremoteSDK.Event.unSnoozeEvent(payload.snoozeRuleId);
    } else {
      // If we don't have a real rule ID, we can log or just ignore it if it was a mocked local state
      console.warn("Attempting to cancel a snooze without a valid snoozeRuleId");
    }

    const state: any = getStoreState();
    const parsedAlerts = state.alerts.parsedAlerts || {};
    const triggerAlert = parsedAlerts[payload.id];
    const alertIdsToCancel: string[] = [];

    if (triggerAlert) {
      const { snoozeScope, snoozeSystemId, snoozeUnitId } = triggerAlert;
      Object.values(parsedAlerts).forEach((alert: any) => {
        if (snoozeScope === "system" && snoozeSystemId) {
          if (alert.snoozeSystemId === snoozeSystemId || alert.systemId === snoozeSystemId || (alert.systemIds && alert.systemIds.includes(snoozeSystemId))) {
            alertIdsToCancel.push(alert.id);
          }
        } else if (snoozeScope === "unit" && snoozeUnitId) {
          if (alert.snoozeUnitId === snoozeUnitId || alert.unitId === snoozeUnitId || (alert.unitIds && alert.unitIds.includes(snoozeUnitId))) {
            alertIdsToCancel.push(alert.id);
          }
        }
      });
    }

    if (alertIdsToCancel.length === 0) {
      alertIdsToCancel.push(payload.id);
    }

    actions._storeCancelSnoozeAlert({ ids: alertIdsToCancel, id: payload.id });
  }),
};

const anomliesTypes: any = { 1: true, 50: true, 201: true, 202: true };

const mapAlertFields = (
  sites: any,
  devices: any,
  systems: any,
  units: any,
  unitsBySyncId: any,
  types: any,
  alert: any,
  sensors: any,
  lines: any,
  powerMeters: any,
  sitesFlags: any,
  groups: any,
  alertLogUnitDisplay: any
) => {
  const { eventTypes, unitTypes, entityTypes } = types;
  const { type, resources = [], status } = alert;

  const fieldsObj: any = {
    isSensor: false,
    eventNames: {
      siteName: "",
      deviceName: "",
      systemName: "",
      unitName: "",
      lineName: "",
      unitNamesTooltip: "",
      unitNames: [],
    },
    eventIds: {
      siteId: "",
      systemId: "",
      unitId: "",
      unitIds: [],
      systemIds: [],
    },
  };

  const isBacNetUnitEvent =
    type === eventTypes.bacnetUnitError ||
    type === eventTypes.bacnetUnitDisconnected ||
    type === eventTypes.bacnetObjectReadingError ||
    type === eventTypes.bacnetObjectPriorityError ||
    (type === eventTypes.controlAction && resources[0]?.type === entityTypes.bacnetUnit);

  if (isBacNetUnitEvent) {
    const site = sites[alert?.site || ''];
    fieldsObj.eventIds.siteId = site?.id;
    fieldsObj.eventNames.siteName = site?.name;
    fieldsObj.eventIds.unitId = resources[0]?.id;
    fieldsObj.eventNames.unitName = resources[0]?.name;
    return fieldsObj;
  }

  // Mirrors isBacNetUnitEvent above: modbus-specific event types plus a
  // controlAction whose resource is a modbus unit. Without listing
  // modbusUnitError here the alert never resolves a siteId and is dropped by
  // the `!eventIds.siteId` guard in the list builder below.
  const isModbusUnitEvent =
    type === eventTypes.modbusUnitError ||
    (type === eventTypes.controlAction && resources[0]?.type === entityTypes.modbusUnit);

  if (isModbusUnitEvent) {
    const site = sites[alert?.site || ''];
    fieldsObj.eventIds.siteId = site?.id;
    fieldsObj.eventNames.siteName = site?.name;
    fieldsObj.eventIds.unitId = resources[0]?.id;
    fieldsObj.eventNames.unitName = resources[0]?.name;
    return fieldsObj;
  }

  if (type === eventTypes.lowLineQualityError) {
    const line = lines[resources[0]?.id] || {};
    const site = sites[line?.site || ""];
    const device = devices[line?.device];

    fieldsObj.eventNames.siteName = site?.name;
    fieldsObj.eventIds.siteId = site?.id;
    fieldsObj.eventNames.deviceName = device?.name;
    fieldsObj.eventNames.deviceId = device?.id;
    fieldsObj.eventIds.systemIds = line?.systems;
    fieldsObj.eventNames.systemName = line?.systems
      ? line?.systems?.length === 1
        ? systems[line?.systems[0]]?.name
        : "multiple"
      : "-";
    fieldsObj.eventNames.lineName = line?.id;

    return fieldsObj;
  }

  if (
    (type === eventTypes.customTelemetry ||
      type === eventTypes.powerMeterDisconnected ||
      type === eventTypes.operationalTelemetry ||
      type === eventTypes.ppdHealthBadPowerMeterData) &&
    powerMeters[resources[0]?.id]
  ) {
    const powerMeter = powerMeters[resources[0]?.id];
    const { site, name, device, id } = powerMeter;
    const { name: siteName = "" } = sites[site] || {};
    const { name: deviceName = "" } = devices[device] || {};

    fieldsObj.eventNames.siteName = siteName;
    fieldsObj.eventIds.siteId = site;
    fieldsObj.eventNames.deviceName = deviceName;
    fieldsObj.eventNames.deviceId = device;
    fieldsObj.eventIds.systemIds = "externals";
    fieldsObj.eventNames.systemName = t`External Sources`;
    fieldsObj.eventNames.unitId = id;
    fieldsObj.eventNames.unitName = name;
    fieldsObj.isPowerMeter = true;
    return fieldsObj;
  }

  if (type === eventTypes.scheduleFailures) {
    if (units[resources[0]?.id]) {
      const item = units[resources[0]?.id];
      const { site = "", name, device = "", id, system = "" } = item;

      fieldsObj.eventIds.siteId = site;
      fieldsObj.eventIds.deviceId = device;
      fieldsObj.eventIds.systemId = system;
      fieldsObj.eventIds.unitId = id;
      fieldsObj.eventNames.siteName = sites[site]?.name;
      fieldsObj.eventNames.deviceName = devices[device]?.name;
      fieldsObj.eventNames.systemName = systems[system]?.name;
      fieldsObj.eventNames.unitName = name;
      fieldsObj.disableDoubleClick = true;
      return fieldsObj;
    }
    if (systems[resources[0]?.id]) {
      const item = systems[resources[0]?.id];
      const { site = "", name, device = "", id } = item;

      fieldsObj.eventIds.siteId = site;
      fieldsObj.eventIds.deviceId = device;
      fieldsObj.eventIds.systemId = id;
      fieldsObj.eventIds.unitId = "";
      fieldsObj.eventNames.siteName = sites[site]?.name;
      fieldsObj.eventNames.deviceName = devices[device]?.name;
      fieldsObj.eventNames.systemName = name;
      fieldsObj.eventNames.unitName = "";
      fieldsObj.disableDoubleClick = true;
      return fieldsObj;
    }
    if (sensors[resources[0]?.id]) {
      const item = sensors[resources[0]?.id];
      const { site = "", name, device = "", id } = item;

      fieldsObj.eventIds.siteId = site;
      fieldsObj.eventIds.deviceId = device;
      fieldsObj.eventIds.systemId = "externals";
      fieldsObj.eventIds.unitId = id;
      fieldsObj.eventNames.siteName = sites[site]?.name;
      fieldsObj.eventNames.deviceName = devices[device]?.name;
      fieldsObj.eventNames.systemName = t`External Sources`;
      fieldsObj.eventNames.unitName = name;
      fieldsObj.disableDoubleClick = true;
      return fieldsObj;
    }
    if (groups[resources[0]?.id]) {
      const item = groups[resources[0]?.id];
      const { site = "", name, id } = item;
      if (!item) {
        return null;
      }

      fieldsObj.eventIds.siteId = site;
      fieldsObj.eventIds.deviceId = "";
      fieldsObj.eventIds.systemId = "";
      fieldsObj.eventIds.unitId = id;
      fieldsObj.eventNames.siteName = sites[site]?.name;
      fieldsObj.eventNames.deviceName = "";
      fieldsObj.eventNames.systemName = "";
      fieldsObj.eventNames.unitName = name;
      fieldsObj.disableDoubleClick = true;
      return fieldsObj;
    }

    return null;
  }

  if (type === eventTypes.commercialAction) {
    return null;
  }

  if (
    (type === eventTypes.serviceAction ||
      type === eventTypes.controlAction ||
      type === eventTypes.smsQuota ||
      anomliesTypes[type]) &&
    resources.length === 0
  ) {
    const site = sites[alert.site || ""];
    fieldsObj.eventNames.siteName = site?.name;
    fieldsObj.eventIds.siteId = site?.id;
    return fieldsObj;
  }
  if (resources.length === 0) {
    return fieldsObj;
  }

  const { eventNames, eventIds } = fieldsObj;
  //device disconnected
  if (
    eventTypes.deviceDisconnected === type ||
    eventTypes.deviceHealthLowIstat === type ||
    eventTypes.deviceHealthLowOstat === type ||
    eventTypes.deviceHealthLowPstat === type ||
    eventTypes.deviceHealthLowLqstat === type ||
    eventTypes.deviceHealthLowSstat === type
  ) {
    const device = devices[resources[0].id];
    eventNames.deviceName = device ? device.serial : resources[0]?.name || "";
    eventNames.siteName =
      (device && device.site && sites[device.site]?.name) || "";
    eventIds.siteId = device ? device.site : "";
    if (status === 1) {
      eventNames.unitName = eventNames.deviceName;
    }
    return fieldsObj;
  }

  const nonUnitResourceResolvers: Array<[any, (item: any) => void]> = [
    [
      systems,
      (item) => {
        fieldsObj.eventIds.systemId = item.id;
        fieldsObj.eventIds.deviceId = item.device;
        fieldsObj.eventNames.systemName = item.name;
      },
    ],
    [
      powerMeters,
      (item) => {
        fieldsObj.eventIds.unitId = item.id;
        fieldsObj.eventNames.unitName = item.name;
        fieldsObj.isPowerMeter = true;
      },
    ],
  ];

  if (resources.length === 1) {
    for (const [store, applyFields] of nonUnitResourceResolvers) {
      const item = store[resources[0]?.id];
      if (!item) {
        continue;
      }
      const { site = "", device = "" } = item;
      fieldsObj.eventIds.siteId = site;
      fieldsObj.eventNames.siteName = sites[site]?.name;
      fieldsObj.eventNames.deviceName = devices[device]?.name;
      fieldsObj.disableDoubleClick = true;
      applyFields(item);
      return fieldsObj;
    }
  }

  let siteId = "";
  let deviceName = "";
  let isSensor = resources[0] && resources[0].type === entityTypes.sensor;
  let items: any = isSensor ? sensors : units;

  for (let i in resources) {
    const { id = "" } = resources[i];
    const storeUnit: any = items[id] || (!isSensor && unitsBySyncId[id]);

    if (storeUnit) {
      let { site = "", system = "", device = "" } = storeUnit;
      if (!isSensor && storeUnit.type === 1 && storeUnit.serviceUnits.length) {
        const serviceUnit = units[storeUnit.serviceUnits[0]];
        system = serviceUnit ? serviceUnit.system : "";
      }

      if (!siteId) {
        siteId = site;
      }

      if (!deviceName) {
        deviceName = devices[device]?.serial || "";
      }

      !isSensor &&
        system &&
        !eventIds.systemIds.includes(system) &&
        eventIds.systemIds.push(system);
      eventIds.unitIds.push(storeUnit.id);
    }
  }

  if (isEmpty(eventIds.unitIds)) {
    return fieldsObj;
  }

  const unitId = eventIds.unitIds.length === 1 ? eventIds.unitIds[0] : "";
  const firstUnitId = eventIds.unitIds[0];
  const isServiceSite = sitesFlags[siteId]?.isServiceSite;
  const firstUnit = items[firstUnitId];
  const smartAlertsTypes: any = {
    206: true,
    209: true,
    210: true,
    208: true,
  };

  if (
    !smartAlertsTypes[type] &&
    !isSensor &&
    !anomliesTypes[type] &&
    ((isServiceSite &&
      firstUnit?.type === unitTypes.indoor &&
      firstUnit?.serviceUnits?.length > 0) ||
      (!isServiceSite && firstUnit?.type !== unitTypes.indoor))
  ) {
    return null;
  }

  const systemId = eventIds.systemIds.length === 1 ? eventIds.systemIds[0] : "";

  const unitNames = eventIds.unitIds.map((id: string) => {
    const item = items[id];
    let name = item?.name || "";
    if (+item?.type === 3 && alertLogUnitDisplay !== "unitName") {
      name = name ? `${name} (${item.address})` : item.address;
    }
    return name;
  });

  let unitName = "";
  let unitNamesTooltip = "";

  if (eventIds.unitIds.length === 1) {
    unitName = unitNames[0];
  } else {
    unitName = "multiple";
    unitNamesTooltip = unitNames.join(", ");
  }

  eventNames.unitName = unitName;
  eventNames.unitNamesTooltip = unitNamesTooltip;
  eventNames.unitNames = unitNames;
  eventNames.systemName = systemId
    ? systems[systemId]?.name
    : isEmpty(eventIds.systemIds)
      ? ""
      : "multiple";
  eventIds.unitId = unitId;
  eventIds.systemId = systemId;
  eventNames.siteName = sites[siteId]?.name || "";
  eventNames.deviceName = deviceName;
  eventIds.siteId = siteId;
  fieldsObj.isSensor = isSensor;
  return fieldsObj;
};

const parseAlerts = (
  additionalAlerts: any = {},
  alertParseOptions: IAlertParseOptions,
  state: any
) => {
  const userPref = state.users.userPreferences;

  if (!state.alerts) return {};
  const {
    openAlertsOnly = false,
    withAllOpenAlerts = true,
    onlyInputAlerts = false,
  } = alertParseOptions || {};
  const { allAlerts: allAlertsState, getAlertFeildsNamesAndIds } =
    state.alerts || {};
  const errorTypes = state.serviceErrorTypes;
  const allCustomers = state.customers?.allCustomers;
  const { allSites, sitesFlags } = state.sites;
  const allUnits = state.units?.allUnits;
  const allSystems = state.systems?.allSystems;
  const {
    eventTypes,
    eventStatusTypes,
    eventTypeStrings,
    eventTypesToSeverityMapping,
  } = state.types || {};

  const eventStatusTypesMirror = state.eventStatusTypesMirror;
  const eventClearTypesMirror = state.eventClearTypesMirror;
  const { timeFormat, dateFormat, users: allUsers } = state.users || {};
  const eventTypesMirror = state.eventTypesMirror;
  const selections = state.selections?.selections;

  const allAlerts = onlyInputAlerts
    ? { ...additionalAlerts }
    : { ...allAlertsState, ...additionalAlerts };
  const parsedAlerts: any = Object.create(allAlerts);
  Object.values(allAlerts).forEach((alert: any) => {
    const alertData = getAlertFeildsNamesAndIds(alert);
    if (!alertData) {
      return;
    }
    if (openAlertsOnly && eventStatusTypesMirror[alert.status] !== "Open") {
      return;
    }
    const { eventNames, eventIds, isSensor, isPowerMeter } = alertData;
    const isUnitsDisconnectedType = eventTypes.unitsDisconnected === alert.type;
    const isLowLineQualityError = eventTypes.lowLineQualityError === alert.type;
    const isUnitDisconnectedType = eventTypes.unitDisconnected === alert.type;
    const isEntitiesNoDateType =
      eventTypes.entitiesHealthLowIstat === alert.type ||
      eventTypes.entitiesHealthLowOstat === alert.type ||
      eventTypes.entitiesHealthLowPstat === alert.type ||
      eventTypes.entitiesHealthLowLqstat === alert.type ||
      eventTypes.entitiesHealthLowSstat === alert.type;

    const isEntityNoDataType =
      eventTypes.entityHealthLowIstat === alert.type ||
      eventTypes.entityHealthLowOstat === alert.type ||
      eventTypes.entityHealthLowPstat === alert.type ||
      eventTypes.entityHealthLowLqstat === alert.type ||
      eventTypes.entityHealthLowSstat === alert.type;

    //error is here
    if (
      (!eventIds.siteId && alert.type !== 204) ||
      isUnitDisconnectedType ||
      isEntityNoDataType ||
      isEntitiesNoDateType
    ) {
      return;
    }
    const timezone = allSites[eventIds.siteId]?.timezone || moment.tz.guess();
    if (!timezone) {
      return;
    }

    const pageFlag = urlFlagMap.alerts;
    if (sitesFlags[eventIds?.siteId]?.[pageFlag] !== true) {
      //hasAlertsLog
      return;
    }

    if (
      !withAllOpenAlerts &&
      !checkDateInRange(
        moment(selections?.dateRange?.startDate).format("MM/DD/YYYY"),
        moment(selections?.dateRange?.endDate).format("MM/DD/YYYY"),
        moment(alert.eventTime).tz(timezone).format("MM/DD/YYYY")
      )
    ) {
      return;
    }

    const severity = severties[eventTypesToSeverityMapping[eventTypesMirror[alert.type]]];

    if (!severity) {
      return;
    }

    const { data, type } = alert;
    // bacnet/modbus unit errors carry the already-formatted value on
    // `data.displayValue` instead of a brand-specific error code.
    const hasDisplayValue =
      type === eventTypes.bacnetUnitError || type === eventTypes.modbusUnitError;
    const errorCode =
      eventTypes.indoorUnitError === type ||
        eventTypes.outdoorUnitError === type
        ? getTrimmedText(data)
        : hasDisplayValue
          ? data?.displayValue
          : '';
    // Modbus also sends the register name in `data.title` ("setpoint L1.203"),
    // which is more specific than the generic trap description.
    let errorDescription = hasDisplayValue
      ? `${(type === eventTypes.modbusUnitError && data?.title) || alert.trapDescription}: ${errorCode}`
      : "";

    if (
      !hasDisplayValue &&
      (errorCode || errorCode === 0)
    ) {
      const brandNum =
        allSystems[eventIds.systemId]?.brandNum ||
        allUnits[eventIds.unitId]?.brand;

      errorDescription = getErrorDescription(
        errorTypes.errorCodeTypes,
        brandNum,
        errorCode
      );
    }
    if (isLowLineQualityError) {
      errorDescription = t`VRF communication line is too noisy. It may impact system controls.

      Potential causes may be improper/loose terminal connection, cut wire, wrong polarity, malfunction, electromagnetic radiation, wrong DIP switch configuration on the device, a problem with indoor unit communication, or others.`;
    }

    if (!!allUnits[eventIds.unitId]) {
      const controlUnitId = allUnits[eventIds.unitId]?.controlUnit;
      const alertUnit = allUnits[eventIds.unitId];
      if (
        !alertUnit?.isVisible ||
        (!!allUnits[controlUnitId] && !allUnits[controlUnitId]?.isVisible)
      ) {
        return;
      }
    }
    const status =
      eventStatusTypesMirror[alert.status] ||
      eventStatusTypesMirror[
      eventStatusTypes[
      alert.status?.charAt(0)?.toLowerCase() + alert.status?.slice(1)
      ]
      ] ||
      "";

    const isAnomalyAlert = severity?.id === "ANOMALIES";
    let finalUnitName = eventNames.unitName || "-";
    if (isAnomalyAlert && eventIds.unitIds.length > 1 && eventNames.unitNames && eventNames.unitNames.length > 0) {
      finalUnitName = `${eventNames.unitNames[0]}...`;
    }

    parsedAlerts[alert.id] = {
      ...alert,
      type: alert.type,
      acknowledgedTime: alert.acknowledgedTimestamp
        ? moment(alert.acknowledgedTimestamp)
          .tz(timezone)
          .format(`${dateFormat}   ${timeFormat}`)
        : false,
      time: moment(alert.eventTime)
        ?.tz(timezone)
        ?.format(`${dateFormat}   ${timeFormat}`),
      clearTime: alert?.clearTime
        ? moment(alert.clearTime)
          ?.tz(timezone)
          ?.format(`${dateFormat}   ${timeFormat}`)
        : null,
      executeTime:
        alert.type === 204
          ? moment(alert.eventTime - 60 * 4 * 1000)
            ?.tz(timezone)
            ?.format(`${dateFormat}   ${timeFormat}`)
          : "",
      status,
      alertType: eventTypeStrings[alert.type],
      siteName: eventNames.siteName,
      unitName: finalUnitName,
      unitNamesTooltip: eventNames.unitNamesTooltip || "",
      unitNames: eventNames.unitNames || [],
      deviceName: eventNames.deviceName,
      lineName: eventNames?.lineName,
      systemName: eventNames.systemName || "-",
      unitIds: eventIds.unitIds,
      systemId: eventIds.systemId,
      customerName:
        (userPref?.nicknames && userPref.nicknames[alert.customer]) ||
        allCustomers[alert.customer]?.name ||
        "",
      clearReason:
        (alert.clearReason && eventClearTypesMirror[alert.clearReason]) || "",
      userText: !isEntitiesNoDateType ? alert.userText : "",
      shortId: alert.shortId,
      alertItemContainerIds: { ...eventIds, customerId: alert.customer },
      description: alert.trapDescription,
      errorCode,
      timestamp: alert.eventTime,
      severity,
      trapTemplateId: alert?.trapTemplateId || "",
      id: alert.id,
      errorDescription,
      alertTypeVal: alert?.type,
      timezone,
      ...eventIds,
      isSensor,
      isPowerMeter,
      acknowledgedDescription: alert?.acknowledgedDescription,
      acknowledgedActor: allUsers[alert?.acknowledgedActorId]?.username,
      closeActor: allUsers[alert?.closeActorId]?.username,
      trap: alert?.trap || null,
      priority: alert?.priority,
      snoozeEndTime: alert?.snoozeEndTimeUTC
        ? moment(alert.snoozeEndTimeUTC)
          ?.tz(timezone)
          ?.format(`${dateFormat}   ${timeFormat}`)
        : null,
      snoozeUsername: alert?.snoozeUsername,
      snoozeRuleId: alert?.snoozeRuleId,
      snoozeScope: alert?.snoozeScope,
      snoozeUnitId: alert?.snoozeUnitId,
      snoozeSystemId: alert?.snoozeSystemId,
    };



  });

  return parsedAlerts;
};

const getErrorDescription = (
  errorCodeTypes: any = {},
  brand: any,
  errorCode: any
) => {
  if (!brand && brand !== 0) {
    return "";
  }

  const brandsErrors = errorCodeTypes[brand] || {};
  if (isEmpty(brandsErrors)) {
    return "";
  }

  return brandsErrors[errorCode] || brandsErrors[+errorCode] || "";
};
